import * as React from 'react';

/**
 * AR-210 (TC-07.4, TC-09.5, TC-11.2, TC-11.3, TC-17.4, TC-27.4, TC-28.3,
 * TC-29.4, TC-35.4, TC-37.4): most AIVIA dialogs are opened from state, not a
 * Radix <DialogTrigger>, so Radix has no trigger to return focus to on close
 * and focus fell to <body>. Remember what had focus when the dialog content
 * mounted and restore it on close. A dialog opened from a menu item (which is
 * gone by then) returns focus to whatever had focus before the menu, normally
 * the menu's trigger. If nothing usable is left, fall back to Radix's own
 * behaviour and, as a last resort, to the page's <main> h1.
 */
const MENU_SELECTOR = '[role="menu"],[role="menubar"],[role="listbox"],[data-radix-popper-content-wrapper]';

// The last two elements focused outside menus. Two, because a dialog field with
// `autoFocus` takes focus while the dialog mounts, before the content ref below
// runs: the latest entry is then that field, and the one before it is the trigger.
let lastFocusOutsideMenus: HTMLElement | null = null;
let previousFocusOutsideMenus: HTMLElement | null = null;
if (typeof document !== 'undefined') {
  document.addEventListener(
    'focusin',
    (event) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        target !== document.body &&
        target !== lastFocusOutsideMenus &&
        !target.closest(MENU_SELECTOR)
      ) {
        previousFocusOutsideMenus = lastFocusOutsideMenus;
        lastFocusOutsideMenus = target;
      }
    },
    true,
  );
}

function usable(el: HTMLElement | null): el is HTMLElement {
  return (
    !!el &&
    el !== document.body &&
    el.isConnected &&
    !(el as HTMLButtonElement).disabled &&
    el.getAttribute('aria-hidden') !== 'true'
  );
}

export function useDialogReturnFocus(onCloseAutoFocus?: (event: Event) => void) {
  const saved = React.useRef<HTMLElement | null>(null);
  const mountedNode = React.useRef<HTMLElement | null>(null);

  // Called with the content node when it mounts (only while open), before
  // Radix moves focus into the dialog. Refs can be re-attached on later
  // renders, by which time focus is inside the dialog: capture once per node.
  const captureOnMount = React.useCallback((node: HTMLElement | null) => {
    if (!node || typeof document === 'undefined') return;
    if (mountedNode.current === node) return;
    mountedNode.current = node;
    const active = document.activeElement as HTMLElement | null;
    const outside = (el: HTMLElement | null) => !!el && !node.contains(el);
    saved.current =
      active && active !== document.body && outside(active) && !active.closest(MENU_SELECTOR)
        ? active
        : [lastFocusOutsideMenus, previousFocusOutsideMenus].find(outside) ?? null;
  }, []);

  const handleCloseAutoFocus = React.useCallback(
    (event: Event) => {
      onCloseAutoFocus?.(event);
      if (event.defaultPrevented) return;
      const el = saved.current;
      if (usable(el)) {
        event.preventDefault();
        el.focus({ preventScroll: true });
        return;
      }
      window.requestAnimationFrame(() => {
        if (document.activeElement && document.activeElement !== document.body) return;
        const heading = document.querySelector<HTMLElement>('main h1');
        if (!heading) return;
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.setAttribute('data-route-focus', '');
        heading.focus({ preventScroll: true });
      });
    },
    [onCloseAutoFocus],
  );

  return { captureOnMount, handleCloseAutoFocus };
}

/** Merge a forwarded ref with a callback ref. */
export function useMergedRef<T>(forwarded: React.ForwardedRef<T>, callback: (node: T | null) => void) {
  return React.useCallback(
    (node: T | null) => {
      callback(node);
      if (typeof forwarded === 'function') forwarded(node);
      else if (forwarded) (forwarded as React.MutableRefObject<T | null>).current = node;
    },
    [forwarded, callback],
  );
}
